import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { MessagesSquare, ArrowLeft, RefreshCw, Loader2, User, Bot, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

export default function Conversations() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [convo, setConvo] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingConvo, setLoadingConvo] = useState(false);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/conversations");
      setList(data);
      if (data.length && !active) setActive(data[0].from_number);
    } catch {
      toast.error("Failed to load conversations");
    } finally {
      setLoadingList(false);
    }
  }, [active]);

  const loadConvo = useCallback(async (number) => {
    if (!number) return;
    setLoadingConvo(true);
    try {
      const { data } = await api.get(`/conversations/${encodeURIComponent(number)}`);
      setConvo(data);
    } catch {
      setConvo(null);
      toast.error("Failed to load conversation");
    } finally {
      setLoadingConvo(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (active) loadConvo(active); }, [active, loadConvo]);

  return (
    <div className="min-h-screen bg-[--w-bg] pb-16 text-[--w-text]">
      <header className="sticky top-0 z-30 border-b border-[--w-border] bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1300px] items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 ring-1 ring-blue-200">
              <MessagesSquare className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold tracking-tight text-[--w-text]">Conversations</h1>
              <p className="font-mono-data text-[11px] text-[--w-text-3]">Per-lawyer WhatsApp chat history</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="refresh-conversations"
              onClick={() => { loadList(); if (active) loadConvo(active); }}
              className="flex items-center gap-1.5 rounded-md bg-stone-700 px-3 py-1.5 text-sm text-white transition-colors hover:bg-stone-600"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <Link
              to="/"
              data-testid="back-to-dashboard"
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-[--w-text-2] transition-colors hover:bg-[--w-card-2] hover:text-[--w-text]"
            >
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-[1300px] grid-cols-1 gap-5 px-5 pt-6 lg:grid-cols-12">
        {/* Lawyer list */}
        <div className="lg:col-span-4">
          <div className="overflow-hidden rounded-lg border border-[--w-border] bg-[--w-card]">
            <div className="flex items-center justify-between border-b border-[--w-border] p-4">
              <span className="font-display text-sm font-bold text-[--w-text]">Lawyers</span>
              <span className="rounded-full bg-stone-100 px-2 py-0.5 font-mono-data text-xs text-[--w-text-2]">{list.length}</span>
            </div>
            <div className="max-h-[70vh] overflow-y-auto" data-testid="conversation-list">
              {loadingList && (
                <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[--w-text-3]" /></div>
              )}
              {!loadingList && list.length === 0 && (
                <p className="px-4 py-10 text-center text-sm text-[--w-text-3]">No conversations yet. They'll appear here when lawyers message on WhatsApp.</p>
              )}
              {list.map((c) => (
                <button
                  key={c.from_number}
                  data-testid={`conversation-item-${c.from_number}`}
                  onClick={() => setActive(c.from_number)}
                  className={`w-full border-b border-[--w-border] px-4 py-3 text-left transition-colors ${
                    active === c.from_number ? "bg-[--w-card-2]" : "hover:bg-[--w-card-2]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate text-sm font-semibold text-[--w-text]">{c.lawyer_name || "Unknown lawyer"}</span>
                    <span className="ml-2 shrink-0 rounded-full bg-stone-100 px-1.5 py-0.5 font-mono-data text-[10px] text-[--w-text-2]">{c.message_count}</span>
                  </div>
                  <div className="mt-0.5 font-mono-data text-[11px] text-blue-600">{c.from_number}</div>
                  <div className="mt-1 flex items-center gap-1 truncate text-xs text-[--w-text-3]">
                    {c.last_role === "lawyer" ? <User className="h-3 w-3 shrink-0" /> : <Bot className="h-3 w-3 shrink-0" />}
                    <span className="truncate">{c.last_message || "—"}</span>
                  </div>
                  {c.pending_case_number && (
                    <span className="mt-1 inline-block rounded bg-amber-50 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-amber-700">
                      awaiting confirm · {c.pending_case_number}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Chat thread */}
        <div className="lg:col-span-8">
          <div className="flex h-[76vh] flex-col overflow-hidden rounded-lg border border-[--w-border] bg-[--w-card]">
            {!active ? (
              <div className="flex flex-1 items-center justify-center text-sm text-[--w-text-3]">Select a lawyer to view the conversation.</div>
            ) : (
              <>
                <div className="flex items-center justify-between border-b border-[--w-border] p-4">
                  <div>
                    <div className="font-display text-base font-bold text-[--w-text]">{convo?.lawyer_name || "Unknown lawyer"}</div>
                    <div className="font-mono-data text-[11px] text-blue-600">{active}</div>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-[--w-text-3]">
                    <Clock className="h-3 w-3" /> {fmt(convo?.updated_at)}
                  </div>
                </div>
                <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-thread">
                  {loadingConvo && (
                    <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[--w-text-3]" /></div>
                  )}
                  {!loadingConvo && convo?.messages?.map((m, i) => {
                    const isLawyer = m.role === "lawyer";
                    const isNote = /^\[.*\]$/.test((m.content || "").trim());
                    if (isNote) {
                      return (
                        <div key={i} className="flex justify-center">
                          <span className="rounded-full bg-stone-100 px-3 py-1 text-[10px] italic text-[--w-text-3]">{m.content.replace(/^\[|\]$/g, "")}</span>
                        </div>
                      );
                    }
                    return (
                      <div key={i} className={`flex ${isLawyer ? "justify-start" : "justify-end"}`}>
                        <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${
                          isLawyer
                            ? "rounded-tl-sm bg-[--w-card-2] text-[--w-text]"
                            : "rounded-tr-sm bg-blue-600 text-white"
                        }`}>
                          <div className="mb-0.5 flex items-center gap-1 text-[10px] opacity-70">
                            {isLawyer ? <User className="h-2.5 w-2.5" /> : <Bot className="h-2.5 w-2.5" />}
                            {isLawyer ? (convo?.lawyer_name || "Lawyer") : "Dicta"}
                          </div>
                          <p className="whitespace-pre-wrap break-words">{m.content}</p>
                          <div className="mt-1 text-right text-[9px] opacity-60">{fmtTime(m.at)}</div>
                        </div>
                      </div>
                    );
                  })}
                  {!loadingConvo && convo && (!convo.messages || convo.messages.length === 0) && (
                    <p className="py-10 text-center text-sm text-[--w-text-3]">No messages in this conversation.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
